import React from 'react';
import { Tooltip } from '@sweep-io/sweep-design';
import { PermissionQuery } from '../../../permissions/permissions.types';
import { usePermissions } from '../../../permissions/usePermissions';

export type AccessValidationType = 'explicit' | 'query';

interface AccessValidationBase {
  type: AccessValidationType;
}



export interface ExplicitAccessValidation extends AccessValidationBase {
  type: 'explicit';
  allowed: boolean;
}

export interface PermissionQueryValidation extends AccessValidationBase {
  type: 'query';
  queries: PermissionQuery[];
}

export type AccessValidation = ExplicitAccessValidation | PermissionQueryValidation;

type PlacementType =
  | 'bottom'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom-end'
  | 'bottom-start'
  | 'left-end'
  | 'left-start'
  | 'right-end'
  | 'right-start'
  | 'top-end'
  | 'top-start';

type ComponentPropForValidation<V extends AccessValidation> =
  V extends ExplicitAccessValidation
  ? (isAllowed: boolean) => React.ReactElement
  : (isAllowed: boolean, allowedPermissions: boolean[]) => React.ReactElement;

interface RestrictedTooltipProps<V extends AccessValidation = AccessValidation> {
  validation: V;
  component?: ComponentPropForValidation<V>;
  notAllowedTitle?: string;
  allowedTitle?: string;
  children?: React.ReactElement;
  forceDisallowTitle?: string; // if present it shows this message even if the permissions are allowed
  placement?: PlacementType;
}

function RestrictedTooltipInner<V extends AccessValidation>(
  {
    validation,
    notAllowedTitle,
    allowedTitle,
    children,
    component,
    forceDisallowTitle,
    placement,
  }: RestrictedTooltipProps<V>,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const { evaluatePermissions: checkPermissions } = usePermissions();

  let allowed = false;
  let permissions: boolean[] | undefined;
  if (validation.type === 'explicit') {
    allowed = validation.allowed;
  } else {
    permissions = checkPermissions(validation.queries);
    allowed = permissions.every(Boolean);
  }

  // If any of the permissions is false it is not allowed
  const _children =
    children ||
    (component &&
      (validation.type === 'explicit'
        ? (component as (isAllowed: boolean) => React.ReactElement)(allowed)
        : (component as (isAllowed: boolean, allowedPermissions: boolean[]) => React.ReactElement)(
          allowed,
          permissions!,
        )));

  if (!_children) {
    throw new Error('Restricted tooltip must contain either children or a component');
  }

  if (allowed && !forceDisallowTitle && !allowedTitle) {
    return <>{_children}</>;
  }

  let _title = allowed ? allowedTitle : notAllowedTitle;
  if (forceDisallowTitle && !_title) {
    _title = forceDisallowTitle;
  }

  return (
    <Tooltip title={_title} placement={placement ?? 'top'} ref={ref}>
      <span>{_children}</span>
    </Tooltip>
  );
}

const RestrictedTooltip = React.forwardRef(RestrictedTooltipInner) as <
  V extends AccessValidation = AccessValidation,
>(
  props: RestrictedTooltipProps<V> & { ref?: React.Ref<HTMLElement> },
) => React.ReactElement | null;

export default RestrictedTooltip;
